import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Project } from '../types';
import EmptyState from '../components/EmptyState';
import EditProjectModal from '../components/EditProjectModal';
import ProjectDetailsModal from '../components/ProjectDetailsModal';

const ROLE_COLORS: Record<string, string> = {
  admin:     '#1C9B82',
  developer: '#3b82f6',
  tester:    '#f59e0b',
};

type SortKey    = 'newest' | 'oldest' | 'name_az' | 'name_za';
type StatusFilter = 'all' | 'active' | 'archived';

interface ProjectWithMeta extends Project {
  company_name?: string;
  creator_name?: string;
  creator_email?: string;
  creator_role?: string;
  issue_count?: number;
}

export default function Dashboard() {
  const { profile, role } = useAuth();
  const { showToast } = useToast();
  const [projects,      setProjects]      = useState<ProjectWithMeta[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [sortKey,       setSortKey]       = useState<SortKey>('newest');
  const [statusFilter,  setStatusFilter]  = useState<StatusFilter>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<ProjectWithMeta[]>('/api/projects');
      setProjects(data);
    } catch {
      showToast('Failed to load projects.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const canCreate = role === 'admin' || role === 'tester' || role === 'developer';

  // Unique companies for the company filter dropdown (only show if > 1 company)
  const uniqueCompanies = Array.from(new Set(projects.map(p => p.company_id).filter(Boolean)));

  const filtered = projects.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) &&
        !(p.description ?? '').toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (companyFilter !== 'all' && p.company_id !== companyFilter) return false;
    return true;
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    switch (sortKey) {
      case 'oldest':  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'name_az': return a.name.localeCompare(b.name);
      case 'name_za': return b.name.localeCompare(a.name);
      default:        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  const active   = statusFilter === 'archived' ? [] : sortedFiltered.filter(p => p.status === 'active');
  const archived = statusFilter === 'active'   ? [] : sortedFiltered.filter(p => p.status === 'archived');

  const hasFilters = search || sortKey !== 'newest' || statusFilter !== 'all' || companyFilter !== 'all';
  const clearFilters = () => { setSearch(''); setSortKey('newest'); setStatusFilter('all'); setCompanyFilter('all'); };

  const handleProjectUpdated = (updated: Project) =>
    setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));

  // Build company name map for the dropdown
  const companyNames: Record<string, string> = {};
  projects.forEach(p => { if (p.company_id && p.company_name) companyNames[p.company_id] = p.company_name; });

  return (
    <div className="page">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">
            {profile?.full_name ?? 'Welcome'} &mdash;{' '}
            {projects.filter(p => p.status === 'active').length} active project{projects.filter(p => p.status === 'active').length !== 1 ? 's' : ''}
          </p>
        </div>
        {canCreate && (
          <Link to="/projects/create" className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Project
          </Link>
        )}
      </div>

      {projects.length > 0 && (
        <div className="project-filter-bar">
          {/* Search */}
          <div className="project-search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="search-input"
              placeholder="Search projects…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Status */}
          <div className="filter-btn-group">
            {(['all', 'active', 'archived'] as StatusFilter[]).map(s => (
              <button
                key={s}
                className={`filter-btn${statusFilter === s ? ' active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Sort */}
          <select
            className="filter-select"
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            aria-label="Sort projects"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name_az">Name A → Z</option>
            <option value="name_za">Name Z → A</option>
          </select>

          {/* Company — only show when projects span multiple companies */}
          {uniqueCompanies.length > 1 && (
            <select
              className="filter-select"
              value={companyFilter}
              onChange={e => setCompanyFilter(e.target.value)}
              aria-label="Filter by company"
            >
              <option value="all">All companies</option>
              {uniqueCompanies.map(cid => (
                <option key={cid} value={cid}>
                  {companyNames[cid] ?? 'Unknown company'}
                </option>
              ))}
            </select>
          )}

          {/* Clear */}
          {hasFilters && (
            <button className="filter-clear-btn" onClick={clearFilters} title="Clear all filters">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Clear
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-grid">
          {[1,2,3].map(i => <div key={i} className="project-card-skeleton" />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description={canCreate ? 'Create your first project to start tracking issues.' : 'No projects have been created yet.'}
          actionLabel={canCreate ? 'Create First Project' : undefined}
          actionTo={canCreate ? '/projects/create' : undefined}
        />
      ) : sortedFiltered.length === 0 ? (
        <EmptyState
          title="No projects match"
          description="Try adjusting your filters or search term."
          actionLabel="Clear filters"
          onAction={clearFilters}
        />
      ) : (
        <>
          {active.length > 0 && (
            <div className="project-grid">
              {active.map(p => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  showCompany={uniqueCompanies.length > 1}
                  onRefresh={fetchProjects}
                  onProjectUpdated={handleProjectUpdated}
                />
              ))}
            </div>
          )}
          {archived.length > 0 && (
            <section style={{ marginTop: 32 }}>
              <h2 className="project-section-title">Archived</h2>
              <div className="project-grid">
                {archived.map(p => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    showCompany={uniqueCompanies.length > 1}
                    onRefresh={fetchProjects}
                    onProjectUpdated={handleProjectUpdated}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ── ProjectCard ───────────────────────────────────────────────
interface CardProps {
  project: ProjectWithMeta;
  showCompany?: boolean;
  onRefresh: () => void;
  onProjectUpdated: (p: Project) => void;
}

const PALETTE = ['#6366f1','#f59e0b','#10b981','#3b82f6','#ec4899','#8b5cf6','#14b8a6','#f97316'];

function ProjectCard({ project, showCompany, onRefresh, onProjectUpdated }: CardProps) {
  const { role, profile } = useAuth();
  const { showToast } = useToast();
  const issueCount = project.issue_count ?? null;
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEdit,    setShowEdit]    = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const canManage = role === 'admin' || profile?.user_id === project.created_by;

  const toggleArchive = async () => {
    const s = project.status === 'active' ? 'archived' : 'active';
    try {
      await api.patch(`/api/projects/${project.id}`, { status: s });
      showToast(`Project ${s}.`, 'success');
      onRefresh();
    } catch {
      showToast('Failed to update project.', 'error');
    }
    setMenuOpen(false);
  };

  const color = PALETTE[project.name.charCodeAt(0) % PALETTE.length];
  const creatorName = project.creator_name?.trim() || project.creator_email?.split('@')[0] || 'Unknown';
  const creatorRole = project.creator_role ?? '';

  const safeLinkHost = project.link
    ? (() => { try { return new URL(project.link!).hostname.replace(/^www\./, ''); } catch { return project.link!; } })()
    : null;

  const handleSaved = (updated: Project) => {
    onProjectUpdated(updated);
    setShowEdit(false);
    showToast('Project updated.', 'success');
  };

  const openEdit = () => { setMenuOpen(false); setShowDetails(false); setShowEdit(true); };

  return (
    <>
      <div className={`project-card${project.status === 'archived' ? ' project-card-archived' : ''}`}>
        <div className="project-card-color-bar" style={{ background: color }} />
        <div className="project-card-body">

          {/* Top row: avatar + menu */}
          <div className="project-card-top">
            <div className="project-avatar" style={{ background: color }}>
              {project.name.charAt(0).toUpperCase()}
            </div>
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button className="icon-btn" onClick={() => setMenuOpen(o => !o)} title="Project options">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                </svg>
              </button>
              {menuOpen && (
                <div className="project-dropdown">
                  <button className="dropdown-item" onClick={() => { setMenuOpen(false); setShowDetails(true); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    View Details
                  </button>
                  {canManage && (
                    <button className="dropdown-item" onClick={openEdit}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit Details
                    </button>
                  )}
                  {canManage && <div className="dropdown-divider" />}
                  {canManage && (
                    <button className="dropdown-item" onClick={toggleArchive}>
                      {project.status === 'active' ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                            <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
                          </svg>
                          Archive project
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                          </svg>
                          Restore project
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Name + description */}
          <Link to={`/projects/${project.id}`} className="project-card-name">{project.name}</Link>
          {project.description && <p className="project-card-desc">{project.description}</p>}

          {/* External link chip */}
          {safeLinkHost && (
            <a
              href={project.link!}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link-chip"
              onClick={e => e.stopPropagation()}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
              {safeLinkHost}
            </a>
          )}

          {/* Creator row */}
          <div className="project-creator-row">
            <div className="project-creator-avatar" style={{ background: ROLE_COLORS[creatorRole] ?? '#6b7280' }}>
              {creatorName.charAt(0).toUpperCase()}
            </div>
            <div className="project-creator-info">
              <span className="project-creator-label">Created by</span>
              <span className="project-creator-name">{creatorName}</span>
            </div>
            {creatorRole && (
              <span className="project-creator-role" style={{ background: ROLE_COLORS[creatorRole] ?? '#6b7280' }}>
                {creatorRole}
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="project-card-footer">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="project-issue-count">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {issueCount === null ? '…' : issueCount} issue{issueCount !== 1 ? 's' : ''}
              </span>
              {project.visibility === 'private' && (
                <span className="project-private-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  Private
                </span>
              )}
              {project.status === 'archived' && <span className="project-archived-badge">Archived</span>}
              {showCompany && project.company_name && (
                <span className="project-company-badge" title={project.company_name}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  {project.company_name}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="project-details-btn" onClick={() => setShowDetails(true)}>
                Details
              </button>
              <Link to={`/projects/${project.id}`} className="project-open-btn">
                Open
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </Link>
            </div>
          </div>

        </div>
      </div>

      {showDetails && (
        <ProjectDetailsModal
          project={project}
          onClose={() => setShowDetails(false)}
          onEdit={openEdit}
        />
      )}
      {showEdit && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEdit(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
